import React, { useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider as MUIThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, ThemeContext } from './context/ThemeContext';
import { useContext } from 'react';
import { LicenseInfo } from '@mui/x-data-grid-pro';
// Layout components
import Layout from './components/layout/Layout';
// Pages
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import AcceptInvite from './pages/AcceptInvite';
import EntityManagement from './pages/EntityManagement';
import EntityGroupsManagement from './pages/EntityGroupsManagement';
import StateManagement from './pages/StateManagement';
import ScenarioManagement from './pages/ScenarioManagement';
import AdjustmentManagement from './pages/AdjustmentManagement';
import AdjustmentInputs from './pages/AdjustmentInputs';
import EntityInputs from './pages/EntityInputs'; // Import the new EntityInputs component
import StateInputs from './pages/StateInputs';
import TaxCalculations from './pages/TaxCalculations';
import StateCalculations from './pages/StateCalculations';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import NotFound from './pages/NotFound';
import BlankLanding from './pages/BlankLanding';
import Access from './pages/Access';
import AdjustmentRollups from './pages/AdjustmentRollups';
import PointInTimeManagement from './pages/PointInTimeManagement';
import SiteManagement from './pages/SiteManagement';
import AuditLog from './pages/AuditLog';
import { canUseInputs, canUseManage, canViewPlatformAdmin } from './config/siteConfig';

// Auth context
import { AuthProvider, useAuth } from './hooks/useAuth';
// PointInTime filter context
import { PointInTimeFilterProvider } from './hooks/usePointInTimeFilter';

// Set MUI X Pro license key 
LicenseInfo.setLicenseKey('99afea76370f6711e12865f41eef25e2Tz0xMTQyNjcsRT0xNzgwODc2Nzk5MDAwLFM9cHJvLExNPXBlcnBldHVhbCxQVj1RMy0yMDI0LEtWPTI=');

// Get CSS variables for theme
const getCssVariable = (variableName) => {
  return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
};

// Theme
const theme = createTheme({
  typography: {
    fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
    button: {
      textTransform: 'none',
      fontFamily: "'Poppins', sans-serif"
    }
  }
});

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AuthorizedRoute = ({ children, canAccess }) => {
  const { isAuthenticated, loading, user, site } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!canAccess(user, site)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <ThemeConsumerApp />
      </ThemeProvider>
    </AuthProvider>);

}

function ThemeConsumerApp() {
  const { themeColor } = useContext(ThemeContext);
  const { site } = useAuth();

  const theme = createTheme({
    palette: {
      primary: {
        main: themeColor || '#868686'
      }
    },
    typography: {
      fontFamily: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
      button: {
        textTransform: 'none',
        fontFamily: "'Poppins', sans-serif"
      }
    }
  });
  return (
    <MUIThemeProvider theme={theme}>
      <CssBaseline />
      <PointInTimeFilterProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/" element={
          <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="calculations" element={<TaxCalculations />} />
            <Route path="state-calculations" element={<StateCalculations />} />
            <Route path="reports" element={<Reports />} />
            <Route path="settings" element={<Settings />} />
            <Route path="analytics" element={<BlankLanding />} />
            {/* Input routes */}
            <Route path="adjustments-inputs" element={<AuthorizedRoute canAccess={canUseInputs}><AdjustmentInputs /></AuthorizedRoute>} />
            <Route path="entity-inputs" element={<AuthorizedRoute canAccess={canUseInputs}><EntityInputs /></AuthorizedRoute>} />
            <Route path="states-inputs" element={<AuthorizedRoute canAccess={canUseInputs}><StateInputs /></AuthorizedRoute>} />

            {/* Management routes */}
            <Route path="scenarios-management" element={<AuthorizedRoute canAccess={canUseManage}><ScenarioManagement /></AuthorizedRoute>} />
            <Route path="adjustments-management" element={<AuthorizedRoute canAccess={canUseManage}><AdjustmentManagement /></AuthorizedRoute>} />
            <Route path="adjustment-rollups" element={<AuthorizedRoute canAccess={canUseManage}><AdjustmentRollups /></AuthorizedRoute>} />
            <Route path="entity-groups-management" element={<AuthorizedRoute canAccess={canUseManage}><EntityGroupsManagement /></AuthorizedRoute>} />
            <Route path="entities-management" element={<AuthorizedRoute canAccess={canUseManage}><EntityManagement /></AuthorizedRoute>} />
            <Route path="states-management" element={<AuthorizedRoute canAccess={canUseManage}><StateManagement /></AuthorizedRoute>} />
            <Route path="point-in-time-management" element={<AuthorizedRoute canAccess={canUseManage}><PointInTimeManagement /></AuthorizedRoute>} />
            <Route path="access" element={<AuthorizedRoute canAccess={canUseManage}><Access /></AuthorizedRoute>} />
            {site?.isGlobal && <Route path="sites" element={<AuthorizedRoute canAccess={canViewPlatformAdmin}><SiteManagement /></AuthorizedRoute>} />}
            {site?.isGlobal && <Route path="audit" element={<AuthorizedRoute canAccess={canViewPlatformAdmin}><AuditLog /></AuthorizedRoute>} />}
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </PointInTimeFilterProvider>
    </MUIThemeProvider>);

}

export default App;
